import { useState, useEffect, useCallback } from 'react'
import {
  Group, Title, Pagination, ActionIcon, Text, Card, Avatar, Image,
  Stack, Divider, Modal, ScrollArea,
  Badge,
} from '@mantine/core'
import { IconTrash, IconHeart, IconMessageCircle, IconBookmark, IconRepeat } from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import ConfirmModal from '@/components/ConfirmModal'
import { api, getFileUrl } from '@/lib/api'
import { formatName } from '@/lib/format'

function timeAgo(dateStr: string) {
  const now = Date.now()
  const diff = now - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'Дөнгөж сая'
  if (mins < 60) return `${mins} мин`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours} цаг`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days} өдөр`
  const months = Math.floor(days / 30)
  return `${months} сар`
}

export default function PostList() {
  const [posts, setPosts] = useState<Record<string, unknown>[]>([])
  const [pagination, setPagination] = useState({ page: 1, pages: 1 })
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [viewPost, setViewPost] = useState<Record<string, unknown> | null>(null)

  const load = useCallback(() => {
    const params: Record<string, string> = { page: String(pagination.page), limit: '20' }
    api.admin.getPosts(params).then((res) => {
      setPosts(res.data)
      setPagination((p) => ({ ...p, pages: res.pagination.pages }))
    })
  }, [pagination.page])

  useEffect(() => { load() }, [load])

  const handleDelete = async () => {
    if (!deleteId) return
    setDeleting(true)
    try {
      await api.admin.deletePost(deleteId)
      notifications.show({ title: 'Амжилттай', message: 'Пост устгагдлаа', color: 'green' })
      load()
    } catch {
      notifications.show({ title: 'Алдаа', message: 'Устгахад алдаа гарлаа', color: 'red' })
    } finally {
      setDeleting(false)
      setDeleteId(null)
    }
  }

  return (
    <div>
      <Title order={2} mb="md">Нийтлэлүүд</Title>

      <Stack gap="md">
        {posts.length === 0 ? (
          <Card shadow="xs" radius="md" withBorder>
            <Text ta="center" c="dimmed" py="xl">Нийтлэл олдсонгүй</Text>
          </Card>
        ) : (
          posts.map((p) => {
            const user = p.userId as Record<string, unknown> | undefined
            const likes = p.likes as unknown[] | undefined
            const comments = p.comments as unknown[] | undefined
            const reposts = p.reposts as unknown[] | undefined
            const bookmarks = p.bookmarks as unknown[] | undefined
            return (
              <Card key={p._id as string} shadow="xs" radius="md" withBorder padding={0} style={{ cursor: 'pointer' }} onClick={() => setViewPost(p)}>
                {/* Header */}
                <Group justify="space-between" p="md" pb={0}>
                  <Group gap="sm" wrap="nowrap">
                    <Avatar src={getFileUrl(user?.avatar as string)} size={44} radius="xl">
                      {formatName(user?.fullName as string)?.charAt(0)}
                    </Avatar>
                    <div>
                      <Group gap={6}>
                       <Text size="sm" fw={600}>{formatName(user?.fullName as string) || '-'}</Text>
                       {(p.isMemberOnly as boolean) && (
                         <Badge size="xs" color="green" variant="light">Зөвхөн гишүүдэд</Badge>
                       )}
                     </Group>
                      <Text size="xs" c="dimmed">{p.createdAt ? timeAgo(p.createdAt as string) : ''}</Text>
                    </div>
                  </Group>
                  <ActionIcon variant="light" color="red" radius="md" onClick={(e) => { e.stopPropagation(); setDeleteId(p._id as string) }}>
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>

                {/* Content */}
                <Text size="sm" px="md" pt="sm" pb={(p.imageUrl as string) ? 'sm' : 0} lineClamp={3} style={{ whiteSpace: 'pre-line' }}>
                  {p.content as string}
                </Text>

                {/* Image */}
                {(p.imageUrl as string) && (
                  <Image src={getFileUrl(p.imageUrl as string)} fit="cover" mah={400} />
                )}

                {/* Stats row */}
                {((likes?.length || 0) > 0 || (comments?.length || 0) > 0) && (
                  <Group px="md" pt="xs" gap="xs">
                    {(likes?.length || 0) > 0 && (
                      <Text size="xs" c="dimmed">{likes?.length} таалагдсан</Text>
                    )}
                    {(likes?.length || 0) > 0 && (comments?.length || 0) > 0 && (
                      <Text size="xs" c="dimmed">·</Text>
                    )}
                    {(comments?.length || 0) > 0 && (
                      <Text size="xs" c="dimmed">{comments?.length} сэтгэгдэл</Text>
                    )}
                  </Group>
                )}

                <Divider mx="md" mt="xs" />

                {/* Action bar */}
                <Group justify="space-around" p="xs">
                  <Group gap={6}>
                    <IconHeart size={18} color="var(--mantine-color-red-5)" />
                    <Text size="sm" fw={500}>{likes?.length || 0}</Text>
                  </Group>
                  <Group gap={6}>
                    <IconMessageCircle size={18} color="var(--mantine-color-blue-5)" />
                    <Text size="sm" fw={500}>{comments?.length || 0}</Text>
                  </Group>
                  <Group gap={6}>
                    <IconRepeat size={18} color="var(--mantine-color-green-5)" />
                    <Text size="sm" fw={500}>{reposts?.length || 0}</Text>
                  </Group>
                  <Group gap={6}>
                    <IconBookmark size={18} color="var(--mantine-color-yellow-6)" />
                    <Text size="sm" fw={500}>{bookmarks?.length || 0}</Text>
                  </Group>
                </Group>
              </Card>
            )
          })
        )}

        {pagination.pages > 1 && (
          <Group justify="center" mt="md">
            <Pagination total={pagination.pages} value={pagination.page} onChange={(p) => setPagination((prev) => ({ ...prev, page: p }))} />
          </Group>
        )}
      </Stack>

      {/* Detail Modal */}
      <Modal
        opened={!!viewPost}
        onClose={() => setViewPost(null)}
        size="lg"
        centered
        padding={0}
        withCloseButton={false}
      >
        {viewPost && (() => {
          const vUser = viewPost.userId as Record<string, unknown> | undefined
          const vLikes = viewPost.likes as unknown[] | undefined
          const vComments = (viewPost.comments as Record<string, unknown>[] | undefined) || []
          const vReposts = viewPost.reposts as unknown[] | undefined
          const vBookmarks = viewPost.bookmarks as unknown[] | undefined
          return (
            <>
              {/* Header */}
              <Group justify="space-between" p="md" pb="sm">
                <Group gap="sm" wrap="nowrap">
                  <Avatar src={getFileUrl(vUser?.avatar as string)} size={48} radius="xl">
                    {formatName(vUser?.fullName as string)?.charAt(0)}
                  </Avatar>
                  <div>
                    <Group gap={6}>
                     <Text size="md" fw={600}>{formatName(vUser?.fullName as string) || '-'}</Text>
                      {(viewPost.isMemberOnly as boolean) && (
                       <Badge size="xs" color="green" variant="light">Зөвхөн гишүүдэд</Badge>
                      )}
                    </Group>
                    <Text size="xs" c="dimmed">{viewPost.createdAt ? timeAgo(viewPost.createdAt as string) : ''}</Text>
                  </div>
                </Group>
                <ActionIcon variant="subtle" c="dimmed" onClick={() => setViewPost(null)}>
                  <Text size="lg">&times;</Text>
                </ActionIcon>
              </Group>

              <Divider />

              {/* Content */}
              <Text size="sm" p="md" style={{ whiteSpace: 'pre-line' }}>
                {viewPost.content as string}
              </Text>

              {/* Image */}
              {(viewPost.imageUrl as string) && (
                <Image src={getFileUrl(viewPost.imageUrl as string)} fit="cover" mah={450} />
              )}

              {/* Stats */}
              <Group px="md" py="xs" gap="lg">
                <Group gap={6}>
                  <IconHeart size={18} color="var(--mantine-color-red-5)" />
                  <Text size="sm" fw={500}>{vLikes?.length || 0}</Text>
                </Group>
                <Group gap={6}>
                  <IconMessageCircle size={18} color="var(--mantine-color-blue-5)" />
                  <Text size="sm" fw={500}>{vComments.length}</Text>
                </Group>
                <Group gap={6}>
                  <IconRepeat size={18} color="var(--mantine-color-green-5)" />
                  <Text size="sm" fw={500}>{vReposts?.length || 0}</Text>
                </Group>
                <Group gap={6}>
                  <IconBookmark size={18} color="var(--mantine-color-yellow-6)" />
                  <Text size="sm" fw={500}>{vBookmarks?.length || 0}</Text>
                </Group>
              </Group>

              <Divider />

              {/* Comments */}
              <Text size="sm" fw={700} px="md" pt="md" pb="xs">
                Сэтгэгдэл ({vComments.length})
              </Text>

              {vComments.length === 0 ? (
                <Text size="sm" c="dimmed" ta="center" py="xl" px="md">
                  Сэтгэгдэл байхгүй
                </Text>
              ) : (
                <ScrollArea h={300} px="md" pb="md">
                  <Stack gap="sm">
                    {vComments.map((c, idx) => {
                      const cUser = c.userId as Record<string, unknown> | undefined
                      return (
                        <Card key={idx} radius="md" bg="var(--mantine-color-gray-0)" padding="sm">
                          <Group gap="sm" mb={4} wrap="nowrap">
                            <Avatar src={getFileUrl(cUser?.avatar as string)} size={30} radius="xl">
                              {formatName(cUser?.fullName as string)?.charAt(0) || '?'}
                            </Avatar>
                            <div style={{ flex: 1 }}>
                              <Group justify="space-between">
                                <Text size="xs" fw={600}>{formatName(cUser?.fullName as string) || 'Хэрэглэгч'}</Text>
                                <Text size="xs" c="dimmed">{c.createdAt ? timeAgo(c.createdAt as string) : ''}</Text>
                              </Group>
                            </div>
                          </Group>
                          <Text size="sm" pl={38}>{c.text as string}</Text>
                        </Card>
                      )
                    })}
                  </Stack>
                </ScrollArea>
              )}
            </>
          )
        })()}
      </Modal>

      <ConfirmModal opened={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} loading={deleting} />
    </div>
  )
}
